import {
  Box,
  Center,
  FormLabel,
  Icon,
  type IconProps,
  Input,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import {
  ChainFullIds,
  ChainNamespaces,
  type Contact,
  isChainAddressValid,
  parseChainAddress,
  parseChainFullId,
} from "@umami/chains";
import { useAllAccounts, useBakerList, useImplicitAccounts } from "@umami/state";
import { get } from "lodash";
import { useId, useState } from "react";
import { type FieldValues, type Path, useFormContext } from "react-hook-form";

import { type BaseProps } from "./BaseProps";
import { getSuggestions } from "./getSuggestions";
import { ChevronDownIcon, StubIcon as XMarkIcon } from "../../assets/icons";
import { useColor } from "../../styles/useColor";
import { AddressTile } from "../AddressTile";
import { Suggestions } from "./Suggestions";
import { getFlattenedAccountsByChains } from "./utilts";

export const AddressAutocomplete = <T extends FieldValues, U extends Path<T>>({
  contacts,
  isDisabled,
  isLoading = false,
  allowUnknown,
  accountsType,
  inputName,
  onUpdate,
  validate,
  label,
  keepValid,
  style,
}: BaseProps<T, U> & { contacts: Contact[] }) => {
  const {
    register,
    setValue,
    formState: { defaultValues },
    getValues,
  } = useFormContext<T>();

  // Cannot avoid this cast because of how types are arranged in UseFormReturn
  const setRealValue = setValue as (
    name: U,
    value: string,
    options: { shouldValidate: boolean }
  ) => void;
  const inputId = useId();
  const color = useColor();

  const [rawValue, setRawValue] = useState(() => {
    if (keepValid || !defaultValues) {
      return "";
    }
    const defaultAddress = get(defaultValues, inputName);

    if (!defaultAddress) {
      return "";
    }

    return contacts.find(c => c.pkh === defaultAddress)?.name || defaultAddress;
  });
  const chainId = getValues("chainId" as Path<T>) || ChainFullIds.TEZOS;
  const [hideSuggestions, setHideSuggestions] = useState(true);
  const [suggestions, setSuggestions] = useState(
    getSuggestions("", contacts, chainId, accountsType)
  );

  const currentRealValue = getValues(inputName);
  const handleChange = (newValue: string) => {
    setRawValue(newValue);
    setSuggestions(getSuggestions(newValue, contacts, chainId, accountsType));

    const contact = contacts.find(
      contact =>
        (contact.name === newValue || contact.pkh === newValue) &&
        contact.chainNamespace === parseChainFullId(chainId).namespace
    );
    let newRealValue;

    if (contact !== undefined) {
      newRealValue = contact.pkh;
      setHideSuggestions(true);
    } else if (allowUnknown && isChainAddressValid(newValue, chainId)) {
      newRealValue = newValue;
    } else if (keepValid) {
      return;
    } else {
      newRealValue = "";
    }

    setRealValue(inputName, newRealValue, { shouldValidate: true });

    if (onUpdate) {
      onUpdate(newRealValue);
    }
  };

  let state = "raw_input";

  if (isLoading) {
    state = "selected_tile";
  } else if (isDisabled) {
    state = "disabled_tile";
  } else if (currentRealValue) {
    state = "selected_tile";
  }

  const clearInput = () => {
    handleChange("");
    setHideSuggestions(false);
  };

  return (
    <Box data-testid={`address-autocomplete-${inputName}`}>
      <FormLabel htmlFor={inputId}>{label}</FormLabel>
      {state === "disabled_tile" && (
        <AddressTile address={parseChainAddress(currentRealValue, chainId)} />
      )}
      {state === "selected_tile" && (
        <Box
          height="48px"
          background={color("100")}
          border="1px solid"
          borderColor={color("300")}
          borderRadius="4px"
          data-testid={`selected-address-tile-${currentRealValue}`}
          onClick={clearInput}
          paddingY={0}
        >
          <Center
            justifyContent="space-between"
            height="100%"
            cursor="pointer"
            data-testid="clear-selected-button"
          >
            <Box width="calc(100% - 40px)">
              <AddressTile
                background="transparent"
                address={parseChainAddress(currentRealValue, chainId)}
                paddingY={{ base: "3px", md: "8px" }}
                size="xs"
              />
            </Box>
            {keepValid ? (
              <Icon as={ChevronDownIcon} marginRight="12px" data-testid="chevron-icon" />
            ) : (
              <CrossButton marginRight="12px" onClick={clearInput} />
            )}
          </Center>
        </Box>
      )}
      {state === "raw_input" && (
        <InputGroup>
          <Input
            {...style}
            paddingRight="40px"
            aria-label={inputName}
            autoComplete="off"
            id={inputId}
            onBlur={e => {
              e.preventDefault();
              setHideSuggestions(true);

              if (keepValid && currentRealValue !== e.target.value) {
                // if the user types something invalid and then blurs, we want to keep the last valid value
                return handleChange(currentRealValue);
              }
              handleChange(e.target.value);
            }}
            onChange={e => handleChange(e.target.value)}
            onFocus={() => setHideSuggestions(false)}
            placeholder="Enter address or contact name"
            value={rawValue}
          />
          <InputRightElement marginRight="12px">
            {rawValue ? (
              <CrossButton onClick={clearInput} />
            ) : (
              <ChevronDownIcon
                data-testid="chevron-icon"
                onClick={() => setHideSuggestions(false)}
              />
            )}
          </InputRightElement>
        </InputGroup>
      )}
      <Input
        {...register<U>(inputName, {
          required: "Invalid address or contact name",
          validate,
        })}
        marginBottom={0}
        data-testid={`real-address-input-${inputName}`}
        name={inputName}
        type="hidden"
      />
      {!hideSuggestions && <Suggestions contacts={suggestions} onChange={handleChange} />}
    </Box>
  );
};

const CrossButton = (props: IconProps) => (
  <Icon
    as={XMarkIcon}
    cursor="pointer"
    data-testid="clear-input-button"
    {...props}
    width="24px"
    height="auto"
  />
);

export const OwnedImplicitAccountsAutocomplete = <T extends FieldValues, U extends Path<T>>(
  props: BaseProps<T, U>
) => {
  const accounts = getFlattenedAccountsByChains(useImplicitAccounts());

  return <AddressAutocomplete {...props} contacts={accounts} />;
};

export const OwnedAccountsAutocomplete = <T extends FieldValues, U extends Path<T>>(
  props: BaseProps<T, U>
) => {
  const accounts = getFlattenedAccountsByChains(useAllAccounts());

  return <AddressAutocomplete {...props} contacts={accounts} />;
};

export const BakersAutocomplete = <T extends FieldValues, U extends Path<T>>(
  props: BaseProps<T, U>
) => {
  const bakers = useBakerList().map(baker => ({
    name: baker.name,
    pkh: baker.address,
    chainNamespace: ChainNamespaces.TEZOS,
  }));

  return <AddressAutocomplete {...props} contacts={bakers} />;
};
